<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, shallowRef, watch } from 'vue'
import { useDataProcessingPolicy } from '~/shared-kernel/presentation/legal/useDataProcessingPolicy'
import {
  CUSTOMER_MENU_PRIMARY,
  CUSTOMER_MENU_SECTIONS,
  customerNavigationTarget,
  isCustomerNavigationActive
} from '~/shared-kernel/presentation/navigation/customer-navigation'

const { t } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const { $customerAccount: account } = useNuxtApp()
const { show: showPolicy } = useDataProcessingPolicy()
const root = shallowRef<HTMLElement | null>(null)
const menuOpen = shallowRef(false)
const openSection = shallowRef<string | null>(null)
let navigationMedia: MediaQueryList | null = null

// The server never has the principal; reading it before mount would desync the hydrated class/attributes,
// which Vue does not patch, and leave the pending state stuck on screen.
const hydrated = shallowRef(false)
const principal = computed(() => hydrated.value ? account.principal.value : null)
const displayName = computed(() => principal.value?.displayName.trim() ?? '')
const firstName = computed(() => displayName.value.split(/\s+/)[0] ?? '')
const initials = computed(() => displayName.value.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]!.toUpperCase()).join(''))
const activeSectionId = computed(() => CUSTOMER_MENU_SECTIONS.find(section =>
  section.destinations.some(destination => isCustomerNavigationActive(route, destination, localePath)))?.id ?? null)

function toggleSection(id: string) {
  openSection.value = openSection.value === id ? null : id
}

watch(() => route.fullPath, () => { menuOpen.value = false })
watch(principal, value => { if (!value) menuOpen.value = false })

function closeMenu(restoreFocus = false) {
  menuOpen.value = false
  if (restoreFocus) void nextTick(() => root.value?.querySelector<HTMLElement>('.account-menu__trigger')?.focus())
}

async function toggleMenu() {
  if (menuOpen.value) return closeMenu(true)
  // Opening the menu unfolds the section the visitor is in, so the current page is always visible.
  openSection.value = activeSectionId.value
  menuOpen.value = true
  await nextTick()
  // Host slots may add links that are hidden at this width; focus must land on one the user can see.
  const links = root.value?.querySelectorAll<HTMLElement>('.account-menu__panel a') ?? []
  Array.from(links).find(link => link.getClientRects().length > 0)?.focus()
}

function closeOnEscape(event: KeyboardEvent) {
  if (event.key !== 'Escape' || !menuOpen.value || event.defaultPrevented) return
  // Nested theme popovers and manuals own their Escape handling.
  if (event.target instanceof Element && event.target.closest('[role="dialog"], [role="menu"]')) return
  closeMenu(true)
}

function closeOnOutsidePointer(event: PointerEvent) {
  if (!menuOpen.value || root.value?.contains(event.target as Node)) return
  if (event.target instanceof Element && event.target.closest('[data-reka-popper-content-wrapper], [role="dialog"]')) return
  closeMenu()
}

function syncNavigationLayout() {
  closeMenu()
}

onMounted(() => {
  hydrated.value = true
  navigationMedia = window.matchMedia('(min-width: 64rem)')
  navigationMedia.addEventListener('change', syncNavigationLayout)
  document.addEventListener('keydown', closeOnEscape)
  document.addEventListener('pointerdown', closeOnOutsidePointer)
})
onBeforeUnmount(() => {
  navigationMedia?.removeEventListener('change', syncNavigationLayout)
  document.removeEventListener('keydown', closeOnEscape)
  document.removeEventListener('pointerdown', closeOnOutsidePointer)
})

async function logout() {
  closeMenu()
  await account.logout()
  // Signing out returns to the public landing, where the restored sign-in access confirms the session ended.
  await navigateTo(localePath('/'))
}
</script>

<template>
  <!-- The trigger renders as soon as a session is known so the topbar keeps its shape while `/me` resolves. -->
  <div v-if="account.signedIn.value" ref="root" class="account-menu" :class="{ 'account-menu--pending': !principal }">
    <TqButton
      class="account-menu__trigger"
      variant="ghost"
      :disabled="!principal"
      :aria-busy="!principal || undefined"
      :aria-expanded="menuOpen"
      aria-controls="customer-account-navigation"
      @click="toggleMenu"
    >
      <span class="account-menu__avatar" aria-hidden="true">{{ initials }}</span>
      <span class="account-menu__name">{{ firstName }}</span>
      <span class="sr-only">{{ t(menuOpen ? 'customer.navigation.menuClose' : 'customer.navigation.menuOpen') }}</span>
      <UIcon class="account-menu__chevron" name="i-lucide-chevron-down" aria-hidden="true" />
    </TqButton>
    <nav
      id="customer-account-navigation"
      class="account-menu__panel tq-navigation-type"
      :class="{ 'account-menu__panel--open': menuOpen }"
      :aria-label="t('customer.rights.accountNavigation')"
      :inert="!menuOpen"
    >
      <div class="account-menu__identity">
        <span class="account-menu__avatar account-menu__avatar--large" aria-hidden="true">{{ initials }}</span>
        <span class="account-menu__identity-text">
          <strong>{{ displayName }}</strong>
          <small>{{ principal?.loginIdentifier }}</small>
        </span>
      </div>
      <slot name="before" :close="closeMenu" />
      <div class="account-menu__group">
        <NuxtLink
          v-for="destination in CUSTOMER_MENU_PRIMARY"
          :key="destination.id"
          class="account-menu__destination"
          :to="customerNavigationTarget(destination, localePath)"
          :aria-current="isCustomerNavigationActive(route, destination, localePath) ? 'page' : undefined"
          @click="closeMenu()"
        >
          <UIcon :name="destination.icon" aria-hidden="true" />
          <span>{{ t(destination.labelKey) }}</span>
        </NuxtLink>
      </div>
      <div class="account-menu__group">
        <div
          v-for="section in CUSTOMER_MENU_SECTIONS"
          :key="section.id"
          class="account-menu__section"
          :class="{ 'account-menu__section--open': openSection === section.id }"
        >
          <TqButton
            class="account-menu__section-toggle"
            variant="ghost"
            :icon="section.icon"
            :aria-expanded="openSection === section.id"
            :aria-controls="`account-menu-section-${section.id}`"
            @click="toggleSection(section.id)"
          >
            <span class="account-menu__section-label">{{ t(section.labelKey) }}</span>
            <span v-if="activeSectionId === section.id" class="account-menu__section-current" aria-hidden="true" />
            <UIcon class="account-menu__section-chevron" name="i-lucide-chevron-down" aria-hidden="true" />
          </TqButton>
          <div :id="`account-menu-section-${section.id}`" class="account-menu__section-body" :inert="openSection !== section.id">
            <div class="account-menu__section-inner">
              <NuxtLink
                v-for="destination in section.destinations"
                :key="destination.id"
                class="account-menu__destination"
                :to="customerNavigationTarget(destination, localePath)"
                :aria-current="isCustomerNavigationActive(route, destination, localePath) ? 'page' : undefined"
                @click="closeMenu()"
              >
                <UIcon :name="destination.icon" aria-hidden="true" />
                <span>{{ t(destination.labelKey) }}</span>
              </NuxtLink>
              <TqButton v-if="section.id === 'profile'" class="account-menu__policy" variant="ghost" icon="i-lucide-file-text" :title="t('dataProcessingPolicy.menuLabel')" @click="showPolicy">{{ t('dataProcessingPolicy.navigationLabel') }}</TqButton>
            </div>
          </div>
        </div>
      </div>
      <div class="account-menu__utility">
        <LocaleSwitcher />
        <ThemePreferenceSelector />
        <div class="account-menu__help"><slot name="help" /></div>
        <TqButton class="account-menu__logout" variant="secondary" icon="i-lucide-log-out" @click="logout">{{ t('actions.logout') }}</TqButton>
      </div>
    </nav>
  </div>
</template>

<style scoped>
/* The panel anchors to the host shell's positioned bar, so the root adds no box of its own. */
.account-menu { display: contents; }
.account-menu > .account-menu__trigger { --tq-ghost-action: transparent; --tq-ghost-action-border: transparent; --tq-on-ghost-action: var(--tq-topbar-text); gap: .45rem; min-height: 2.75rem; border-radius: 999px; padding: .25rem; }
.account-menu > .account-menu__trigger:focus-visible, .account-menu__panel :deep(a:focus-visible) { outline: 2px solid var(--tq-focus); outline-offset: 2px; }
.account-menu__avatar { display: grid; place-items: center; flex: 0 0 auto; width: 2.25rem; height: 2.25rem; border-radius: 50%; background: var(--tq-nav-active-bg); color: var(--tq-nav-active-text); font-size: .78rem; font-weight: 700; letter-spacing: .02em; }
.account-menu__avatar--large { width: 2.75rem; height: 2.75rem; font-size: .95rem; }
.account-menu__name { display: none; max-width: 9rem; overflow: hidden; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
/* Pending identity: hold roughly a first name's width so the controls beside it do not shift. */
.account-menu--pending .account-menu__name { min-width: 3.5rem; height: .9rem; border-radius: var(--tq-radius-pill); background: var(--tq-nav-active-bg); opacity: .35; }
.account-menu--pending .account-menu__avatar { opacity: .55; }
/* The placeholder must vanish in one frame: fading it would show the name over a dimming pill. */
.account-menu__name, .account-menu__avatar { transition: none; }
.account-menu > .account-menu__trigger:disabled { opacity: 1; cursor: progress; }
.account-menu__chevron { width: 1rem; height: 1rem; }
.account-menu__trigger[aria-expanded="true"] .account-menu__chevron { transform: rotate(180deg); }
.account-menu__panel { position: absolute; z-index: 50; top: calc(100% + .5rem); right: var(--account-menu-inset, clamp(.75rem, 1.5vw, 1.75rem)); display: none; width: min(24rem, calc(100vw - 1.5rem)); max-height: calc(100dvh - 6.5rem - var(--tq-bottom-navigation-offset, 0px)); overflow-y: auto; overscroll-behavior: contain; border: 1px solid var(--tq-nav-border); border-radius: var(--tq-radius-lg); background: var(--tq-nav-bg); color: var(--tq-nav-text); padding: .5rem; box-shadow: var(--tq-shadow-soft); text-align: start; }
.account-menu__panel--open { display: grid; grid-auto-rows: max-content; align-content: start; gap: .4rem; touch-action: pan-y; }
.account-menu__identity { display: flex; align-items: center; gap: .75rem; min-width: 0; padding: .45rem .5rem .55rem; }
.account-menu__identity-text { display: grid; min-width: 0; }
.account-menu__identity-text > * { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.account-menu__identity-text strong { font-weight: 600; }
.account-menu__identity-text small { color: var(--tq-nav-text-muted); font-size: .8rem; }
.account-menu__group { display: grid; grid-template-columns: minmax(0, 1fr); gap: .1rem; border-top: 1px solid var(--tq-nav-border); padding-top: .4rem; }
.account-menu__panel :deep(a) { display: flex; align-items: center; gap: .65rem; min-height: 2.75rem; border-radius: var(--tq-radius-md); padding: .5rem .6rem; color: var(--tq-nav-text); font-weight: var(--tq-font-weight-navigation); text-decoration: none; overflow-wrap: anywhere; }
.account-menu__panel :deep(a > .iconify) { flex: 0 0 auto; width: 1.1rem; height: 1.1rem; }
.account-menu__panel :deep(a:hover) { background: var(--tq-nav-hover-bg); }
.account-menu__panel :deep(a[aria-current]) { background: var(--tq-nav-active-bg); color: var(--tq-nav-active-text); }
.account-menu__section-toggle, .account-menu__policy { --tq-ghost-action: transparent; --tq-ghost-action-border: transparent; --tq-on-ghost-action: var(--tq-nav-text); justify-content: flex-start; width: 100%; min-height: 2.75rem; border-radius: var(--tq-radius-md); padding: .5rem .6rem; font-weight: var(--tq-font-weight-navigation); }
.account-menu__section-toggle :deep(.tq-button__content), .account-menu__policy :deep(.tq-button__content) { gap: .65rem; }
.account-menu__section-toggle :deep(.tq-button__content) { flex: 1; min-width: 0; }
.account-menu__section-toggle:hover { --tq-ghost-action: var(--tq-nav-hover-bg); }
.account-menu__section-label { flex: 1; min-width: 0; text-align: start; }
/* Marks the folded section that holds the current page. */
.account-menu__section-current { width: .45rem; height: .45rem; border-radius: 50%; background: var(--tq-nav-active-bg); }
.account-menu__section--open .account-menu__section-current { display: none; }
.account-menu__section-chevron { flex: 0 0 auto; width: 1rem; height: 1rem; color: var(--tq-nav-text-muted); transition: transform 220ms var(--tq-ease-out); }
.account-menu__section--open .account-menu__section-chevron { transform: rotate(180deg); }
/* 0fr -> 1fr animates to the content's real height without measuring it; visibility flips after the collapse. */
.account-menu__section-body { display: grid; grid-template-rows: 0fr; opacity: 0; visibility: hidden; transition: grid-template-rows 240ms var(--tq-ease-out), opacity 180ms var(--tq-ease-out), visibility 0s linear 240ms; }
.account-menu__section--open .account-menu__section-body { grid-template-rows: 1fr; opacity: 1; visibility: visible; transition-delay: 0s; }
.account-menu__section-inner { display: grid; gap: .1rem; min-height: 0; overflow: hidden; margin-inline-start: 1.15rem; border-inline-start: 1px solid var(--tq-nav-border); padding-inline-start: .45rem; }
.account-menu__section--open .account-menu__section-inner { padding-block: .15rem .3rem; }
@media (prefers-reduced-motion: reduce) {
  .account-menu__section-body, .account-menu__section-chevron { transition: none; }
}
/* Language, theme, help and logout keep one solid elevated surface even inside a dark tenant menu. */
.account-menu__utility { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: .5rem; border-radius: var(--tq-radius-md); background: var(--tq-bg-elevated); color: var(--tq-text); padding: .5rem; }
.account-menu__utility :deep(.theme-preference-selector) { width: 100%; min-width: 0; justify-content: center; }
.account-menu__utility > .account-menu__logout { grid-column: 1 / -1; width: 100%; }
.account-menu__help { display: flex; justify-content: center; }
.account-menu__help:empty { display: none; }
@media (min-width: 64rem) {
  .account-menu__name { display: inline; }
  .account-menu--pending .account-menu__name { display: inline-block; }
  .account-menu > .account-menu__trigger { padding-inline: .25rem .6rem; }
  .account-menu__panel { width: 21rem; max-height: calc(100dvh - 6.5rem); }
  .account-menu__panel :deep(a), .account-menu__section-toggle, .account-menu__policy { min-height: 2.4rem; padding-block: .4rem; font-size: .875rem; }
}
</style>
